<template>
  <div>
    <v-layout
      v-if="!hideTitle"
      class="p-4 pr-0 border-bottom-light-grey min-height-57px"
    >
      <v-flex class="d-flex font-level-3-bold my-auto">
        <span class="detail-svg-icon mr-2">
          <!--begin::Svg Icon-->
          <span class="svg-icon svg-icon-lg mr-2 svg-v-list-icon">
            <inline-svg :src="$assetURL('media/custom-svg/attachment.svg')" />
          </span>
          <!--end::Svg Icon-->
        </span>
        <h1 class="form-title font-size-20 font-weight-700">
          <template v-if="typeText">
            {{ typeText }}
          </template>
          <template v-else> Files </template>
        </h1>
      </v-flex>
      <v-flex class="text-right">
        <v-btn
          v-if="typeText != 'visit' && !hideAttach"
          v-on:click="add_document_dialog = true"
          color="cyan text-white"
          class="ml-2"
          depressed
          tile
          ><v-icon small left>mdi-playlist-plus</v-icon>Attach File
        </v-btn>
      </v-flex>
    </v-layout>
    <div class="overflow-y position-relative">
      <v-progress-linear
        v-if="isReordering"
        indeterminate
        color="cyan"
        height="3"
        class="position-absolute-top"
      ></v-progress-linear>
      <template v-if="dbFiles.length">
        <div class="doc-table-card">
          <!-- Table Header Bar -->
          <div
            class="doc-table-header d-flex align-center px-4 py-2 border-bottom"
          >
            <!-- Left Checkbox Column -->
            <div
              style="width: 36px; min-width: 36px"
              class="d-flex align-center justify-center"
              v-if="allowReorder"
            >
              <v-icon style="font-size: 20px; color: #64748b"
                >mdi-checkbox-blank-outline</v-icon
              >
            </div>
            <!-- Spacer for Thumbnail -->
            <!-- <div style="width: 52px; min-width: 52px"></div> -->
            <!-- Column 1: File Details -->
            <div
              class="px-3 flex-grow-1 doc-header-title"
              style="min-width: 220px"
            >
              File Details
            </div>
            <!-- Column 2: Validity / Tags -->
            <div
              class="px-2 doc-header-title d-flex align-center"
              style="width: 335px; min-width: 295px"
            >
              Validity / Tags
            </div>
            <!-- Optional Column: Append to PDF -->
            <div
              v-if="showAppendToPdf"
              class="px-2 doc-header-title append-pdf-column-header"
            >
              Append to PDF
            </div>
            <!-- Column 3: Status & Actions -->
            <div
              class="px-4 doc-header-title"
              style="width: 280px; min-width: 260px"
            >
              Status & Actions
            </div>
          </div>

          <!-- Draggable Rows -->
          <draggable
            v-model="dbFiles"
            :disabled="!allowReorder || dbFiles.length <= 1 || isReordering"
            handle=".document-drag-handle"
            animation="200"
            ghost-class="document-ghost"
            chosen-class="document-chosen"
            drag-class="document-drag-active"
            @start="isDragging = true"
            @end="isDragging = false"
            @change="onReorderFiles"
          >
            <div
              v-for="(row, index) in dbFiles"
              :key="row.id || index"
              class="border-bottom d-flex align-center py-3 px-4 doc-row"
              :class="{
                'doc-row-highlight': row.extension && isImage(row.extension),
              }"
              style="min-height: 64px"
            >
              <!-- Drag Handle (6 dots) -->
              <div
                v-if="allowReorder && dbFiles.length > 1"
                class="d-flex align-center justify-center document-drag-handle-col"
                style="width: 36px; min-width: 36px"
              >
                <v-icon
                  class="document-drag-handle cursor-move"
                  style="font-size: 20px; color: #94a3b8 !important"
                  title="Drag to reorder"
                >
                  mdi-dots-grid
                </v-icon>
              </div>
              <!-- <div v-else style="width: 36px; min-width: 36px"></div> -->

              <!-- File Icon / Thumbnail -->
              <div
                class="d-flex align-center justify-center"
                style="width: 52px; min-width: 52px"
              >
                <div
                  class="doc-icon-wrapper d-flex align-center justify-center"
                >
                  <template v-if="row.extension && isImage(row.extension)">
                    <ImageTemplate
                      :src="row.file.url"
                      style="
                        max-width: 40px;
                        width: 40px;
                        height: 40px;
                        object-fit: cover;
                        border-radius: 6px;
                      "
                    >
                    </ImageTemplate>
                  </template>
                  <template v-else>
                    <inline-svg
                      style="max-width: 40px; width: 40px; height: 40px"
                      :src="$assetURL(`media/mime/${row.extension}.svg`)"
                    />
                  </template>
                </div>
              </div>

              <!-- Name and Metadata -->
              <div class="px-3 flex-grow-1" style="min-width: 220px">
                <div class="d-flex align-center text-truncate">
                  <span
                    class="doc-title-link cursor-pointer font-weight-700 font-size-14 text-truncate"
                    :title="row.name"
                    v-on:click="doAction(row, 'download')"
                  >
                    {{ row.name }}
                  </span>
                  <v-chip
                    v-if="
                      (row &&
                        row.type &&
                        (row.type == 2 ||
                          row.type == 251 ||
                          row.type == 250 ||
                          row.type == 302 ||
                          row.type == 301 ||
                          row.type == 678)) ||
                      row.type == 5005 ||
                      row.type == 5006 ||
                      row.type == 5007 ||
                      row.type == 5008 ||
                      row.type == 5015
                    "
                    :color="getTypeColor(row.type)"
                    outlined
                    label
                    small
                    class="ml-2 font-weight-600 px-2"
                    style="height: 20px !important; font-size: 11px !important"
                  >
                    {{ getTypeName(row.type) }}
                  </v-chip>
                </div>
                <div
                  class="d-flex align-center flex-wrap font-size-12 mt-1 doc-sub-details"
                >
                  <span v-if="row.document_no">
                    <strong class="color-custom-blue">Doc #:</strong>
                    {{ row.document_no }}
                  </span>
                  <span
                    v-if="row.document_no && (row.document_type || row.size)"
                    class="doc-pipe-divider"
                    >|</span
                  >
                  <span v-if="row.document_type">
                    <strong class="color-custom-blue">Type:</strong>
                    {{
                      row.document_type === "Others"
                        ? row.document_type_other || "Others"
                        : row.document_type
                    }}
                  </span>
                  <span
                    v-if="row.document_type && row.size"
                    class="doc-pipe-divider"
                    >|</span
                  >
                  <span v-if="row.size">
                    <strong class="color-custom-blue">Size:</strong>
                    {{ (Number(row.size) / 1024).toFixed(2) }} KB
                  </span>
                  <template
                    v-if="
                      row.super_parent != null && Number(row.super_parent) !== 0
                    "
                  >
                    <span class="doc-pipe-divider">|</span>
                    <span class="color-custom-blue font-weight-600">
                      {{ row.super_parent }}
                    </span>
                  </template>
                  <template v-if="row.type_in_module">
                    <span class="doc-pipe-divider">|</span>
                    <span class="red--text font-weight-500">
                      ({{ row.type_in_module }} - {{ row.module_type_parent }})
                    </span>
                  </template>
                </div>
              </div>

              <!-- Dates Column -->
              <div
                style="width: 180px; min-width: 160px"
                class="px-2"
                v-if="typeText != 'Leave'"
              >
                <div v-if="!extensive" class="font-size-12">
                  <template
                    v-if="
                      row?.start_date || row?.end_date || row?.reminder_date
                    "
                  >
                    <div class="d-flex align-start">
                      <v-icon
                        color="cyan darken-2"
                        class="mr-2 mt-1"
                        style="font-size: 19px"
                        >mdi-calendar-month-outline</v-icon
                      >
                      <div style="line-height: 1.45">
                        <div v-if="row?.start_date">
                          <span
                            class="color-custom-blue font-weight-600 d-inline-block"
                            style="min-width: 58px"
                            >Start:</span
                          >
                          <span>{{ formatDate(row.start_date) }}</span>
                        </div>
                        <div v-if="row?.end_date">
                          <span
                            class="color-custom-blue font-weight-600 d-inline-block"
                            style="min-width: 58px"
                            >End:</span
                          >
                          <span>{{ formatDate(row.end_date) }}</span>
                        </div>
                        <div v-if="row?.reminder_date">
                          <span
                            class="color-custom-blue font-weight-600 d-inline-block"
                            style="min-width: 58px"
                            >Reminder:</span
                          >
                          <span>{{ formatDate(row.reminder_date) }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <div
                    v-else
                    class="d-flex align-center text-muted font-size-12"
                  >
                    <v-icon small color="#94a3b8" class="mr-1"
                      >mdi-calendar-blank-outline</v-icon
                    >
                    <span>No dates</span>
                  </div>
                </div>
              </div>

              <!-- Tags Column -->
              <div
                style="width: 155px; min-width: 135px"
                class="px-2"
                :class="{ 'mr-2': !extensive }"
              >
                <template v-if="row.tags && row.tags?.length > 0">
                  <div
                    class="d-flex flex-wrap align-center"
                    style="gap: 4px; max-width: 145px"
                  >
                    <v-chip
                      v-for="(tagRow, index) in row.tags"
                      :key="index"
                      small
                      class="font-weight-600 px-2"
                      text-color="white"
                      :color="tagRow?.color || 'cyan'"
                      style="
                        height: 20px !important;
                        border-radius: 12px !important;
                        font-size: 11px !important;
                      "
                    >
                      {{ tagRow.tag }}
                    </v-chip>
                  </div>
                </template>
                <div v-else class="d-flex align-center text-muted font-size-12">
                  <v-icon small color="#94a3b8" class="mr-1"
                    >mdi-tag-outline</v-icon
                  >
                  <span>No tags</span>
                </div>
              </div>

              <!-- Append to PDF Column -->
              <div v-if="showAppendToPdf" class="px-2 append-pdf-column">
                <v-tooltip v-if="isImage(row.extension)" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-simple-checkbox
                      v-model="row.append_to_pdf"
                      v-bind="attrs"
                      v-on="on"
                      color="green"
                      on-icon="mdi-checkbox-marked-circle"
                      off-icon="mdi-checkbox-blank-circle-outline"
                      aria-label="Append to PDF"
                      @input="updateAppendToPdf(row)"
                    ></v-simple-checkbox>
                  </template>
                  <span>Append to PDF</span>
                </v-tooltip>
                <v-tooltip v-else top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="append-pdf-unavailable"
                      color="red"
                      size="24"
                      aria-label="Cannot append to PDF"
                      >mdi-minus-circle</v-icon
                    >
                  </template>
                  <span>Cannot append to PDF</span>
                </v-tooltip>
              </div>

              <!-- Status & Actions Column: Uploaded By & Actions with vertical divider -->
              <div
                style="width: 280px; min-width: 260px"
                class="px-4 d-flex align-center"
              >
                <!-- Uploaded By & Date -->
                <div
                  class="d-flex flex-column align-center"
                  style="min-width: 85px"
                >
                  <v-chip
                    v-if="row.user_display_name"
                    class="my-0 px-3"
                    color="cyan darken-1"
                    text-color="white"
                    small
                    style="
                      height: 22px !important;
                      border-radius: 12px !important;
                      font-size: 11px !important;
                      font-weight: 600;
                    "
                  >
                    <v-icon x-small left color="white" class="mr-1"
                      >mdi-account</v-icon
                    >
                    <span class="custom-nowrap-ellipsis">
                      {{ row.user_display_name }}
                    </span>
                  </v-chip>
                  <div
                    v-if="row.added_at"
                    class="font-size-12 text-muted mt-1 text-center"
                  >
                    {{ formatDate(row.added_at) }}
                  </div>
                </div>

                <!-- Vertical Divider Line -->
                <div class="doc-vertical-divider mx-4"></div>

                <!-- Action Buttons -->
                <div class="document-action-group d-flex align-center">
                  <v-btn
                    v-on:click="doAction(row, 'download')"
                    icon
                    small
                    depressed
                    color="blue darken-3"
                    class="mr-2"
                    title="Download"
                  >
                    <v-icon style="font-size: 20px">mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                    v-on:click="doAction(row, 'delete')"
                    icon
                    small
                    depressed
                    class="mx-1"
                    color="red lighten-1"
                    title="Delete"
                  >
                    <v-icon style="font-size: 20px">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </template>
      <v-row v-else>
        <v-col class="text-center border-top">
          <p class="m-0 py-15 row-not-found text-center">
            <img
              :src="$assetURL('media/error/empty.png')"
              class="row-not-found-image"
            />
            Uhh... There are no file at the moment.
          </p>
        </v-col>
      </v-row>

      <table width="100%" class="detail-table table-head-sticky" v-if="false">
        <thead>
          <tr style="border: 1px solid lightgray">
            <th class="p-2 light-blue-bg" width="200"></th>
            <th class="p-2 light-blue-bg" width="250">Name</th>
            <th
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 light-blue-bg"
              width="200"
            >
              Dates
            </th>
            <th
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 light-blue-bg"
              width="300"
            >
              Tags
            </th>
            <th
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 light-blue-bg"
              width="300"
            >
              Description
            </th>
          </tr>
        </thead>

        <tbody v-if="dbFiles.length">
          <tr
            v-for="(row, index) in dbFiles"
            :key="index"
            style="border: 1px solid lightgray"
          >
            <td class="p-2 border-top-light-grey" width="50">
              <template v-if="row.extension && isImage(row.extension)">
                <ImageTemplate
                  :src="row.file.url"
                  style="max-width: 50px; width: 50px"
                ></ImageTemplate>
              </template>
              <template v-else>
                <inline-svg
                  style="max-width: 50px; width: 50px"
                  :src="$assetURL(`media/mime/${row.extension}.svg`)"
                />
              </template>
            </td>
            <td class="p-2 border-top-light-grey" width="250">
              <p
                class="m-0 blue--text font-level-1 cursor-pointer bold-600"
                v-on:click="doAction(row, 'download')"
              >
                {{ row.name }}
              </p>
              <div v-if="is_main" class="d-flex my-1">
                <p class="m-0 font-level-1 cursor-pointer bold-600">Type :</p>
                <v-badge
                  class="mt-2 ml-2"
                  :color="row.type_color ? row.type_color : '#58b5bd'"
                  :content="row.type.toUpperCase()"
                ></v-badge>
              </div>

              <p class="m-0 text-muted font-small w-100">
                {{ formatDate(row.added_at) }}
              </p>
              <p class="m-0 text-muted font-small w-100">
                by {{ row.user_display_name }}
              </p>
            </td>

            <td
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 border-top-light-grey"
            >
              <template v-if="row.start_date && row.end_date">
                <div class="d-flex align-items-center">
                  <div
                    :class="`${
                      row.end_date && checkWExpDate(row.end_date)
                        ? 'expired'
                        : ''
                    }`"
                  >
                    <div class="d-flex align-items-center">
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <label
                            v-bind="attrs"
                            v-on="on"
                            class="font-size-14 mb-1 mr-7"
                          >
                            <v-badge
                              color="cyan"
                              content="S"
                              class="badge-new"
                            ></v-badge>
                          </label>
                        </template>
                        <span> Start Date </span>
                      </v-tooltip>
                      <v-chip label class="mb-2">
                        {{ formatDate(row.start_date) }}
                      </v-chip>
                    </div>

                    <div class="d-flex align-items-center">
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <label
                            v-bind="attrs"
                            v-on="on"
                            class="font-size-14 mb-1 mr-7"
                          >
                            <v-badge
                              color="red"
                              content="E"
                              class="badge-new"
                            ></v-badge>
                          </label>
                        </template>
                        <span> End Date </span>
                      </v-tooltip>
                      <v-chip label class="mb-2">
                        {{ formatDate(row.end_date) }}
                      </v-chip>
                    </div>
                    <div class="d-flex align-items-center">
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <label
                            v-bind="attrs"
                            v-on="on"
                            class="font-size-14 mb-1 mr-7"
                          >
                            <v-badge
                              color="orange"
                              content="R"
                              class="badge-new"
                            ></v-badge>
                          </label>
                        </template>
                        <span> Reminder Date </span>
                      </v-tooltip>
                      <v-chip label class="mb-2">
                        {{ formatDate(row.reminder_date) }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <em class="text--secondary">No Dates</em>
              </template>
            </td>

            <td
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 border-top-light-grey"
            >
              <template v-if="row.tags && row.tags.length > 0">
                <v-chip
                  v-for="(tagRow, index) in row.tags"
                  :key="`tag-${index}`"
                  class="ma-2 tag"
                  size="x-small"
                  color="#58b5bd"
                  text-color="white"
                >
                  {{ tagRow.text }}
                </v-chip>
              </template>
              <template v-else>
                <em class="text--secondary">No Tags Data</em>
              </template>
            </td>

            <td
              v-if="extensive && typeText != 'Technicians'"
              class="p-2 border-top-light-grey font-level-1"
              width="50%"
            >
              <div class="d-flex justify-space-between">
                <template
                  v-if="row && row.description && row.description != 'null'"
                >
                  {{ row.description }}
                </template>
                <template v-else>
                  <em class="text--secondary"> No Description</em>
                </template>
                <div md1>{{ getFileSize(row.size) }} KB</div>
              </div>
            </td>

            <td class="p-2 border-top-light-grey font-level-1" width="150">
              <v-btn
                v-on:click="doAction(row, 'download')"
                icon
                depressed
                color="blue darken-4"
                class="mr-2"
                ><v-icon medium>mdi-download</v-icon></v-btn
              >
              <v-btn
                v-on:click="doAction(row, 'delete')"
                icon
                depressed
                color="red lighten-1"
                ><v-icon medium>mdi-delete</v-icon></v-btn
              >
            </td>
          </tr>
        </tbody>
        <tfoot v-else>
          <tr>
            <td colspan="6">
              <p class="m-0 row-not-found text-center">
                <img
                  :src="$assetURL('media/error/empty.png')"
                  class="row-not-found-image"
                />
                Uhh... There are no file at the moment.
              </p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <DocumentwithName
      :document-dialog="add_document_dialog"
      v-on:close="add_document_dialog = false"
      v-on:success="getFiles()"
      :document-type="type"
      :document-id="typeId"
      :extensive="extensive"
      :parent-type="typeParent"
      :parent-type-id="typeParentId"
      :tag-type="tagType"
      :show-append-to-pdf="showAppendToPdf"
    >
    </DocumentwithName>
    <Dialog :commonDialog="deleteDialog" :dialog-width="600">
      <template v-slot:title> Delete File</template>
      <template v-slot:body>
        <v-row class="delete-dialog">
          <v-col md="2" class="py-0 text-right my-auto">
            <span class="svg-icon svg-icon-lg delete-confirm-icon">
              <!--begin::Svg Icon-->
              <inline-svg
                :src="$assetURL('media/custom-svg/attention-circle.svg')"
              />
              <!--end::Svg Icon-->
            </span>
          </v-col>
          <v-col md="10" class="py-0 my-auto">
            <p class="btx-p m-0">
              Deleting File is irreversible, Are you sure about deleting it?
            </p>
          </v-col>
        </v-row>
      </template>
      <template v-slot:action>
        <v-btn
          class="white--text"
          :loading="deleteLoading"
          :disabled="deleteLoading"
          depressed
          color="red lighten-1"
          tile
          v-on:click="deleteFile()"
        >
          Yes! Delete
        </v-btn>
        <v-btn
          depressed
          tile
          :disabled="deleteLoading"
          v-on:click="deleteDialog = false"
        >
          No, Close
        </v-btn>
      </template>
    </Dialog>
  </div>
</template>
<script>
import { toNumber, round, find } from "lodash";
import ImageTemplate from "@/view/pages/Image";
//import Dialog from "@/view/components/Dialog";
import Dialog from "@/view/pages/partials/Dialog.vue";
//import TextAreaInput from "@/view/components/TextAreaInput";
import ApiService from "@/core/services/api.service";
import DocumentwithName from "@/view/pages/DocumentwithName";
//import { EventBus } from "@/core/event-bus/event.bus";
import moment from "moment-timezone";
moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);
import CommonMixin from "@/core/plugins/common-mixin";
import draggable from "vuedraggable";
export default {
  mixins: [CommonMixin],
  props: {
    tagType: {
      type: String,
      default: null,
    },
    typeText: {
      type: String,
      default: null,
    },
    type: {
      type: Number,
      default: null,
    },
    typeParent: {
      type: String,
      default: null,
    },
    typeUuid: {
      type: String,
      default: null,
    },
    typeId: {
      type: Number,
      default: null,
    },
    typeParentId: {
      type: Number,
      default: null,
    },
    reload: {
      type: Boolean,
      default: false,
    },
    extensive: {
      type: Boolean,
      default: false,
    },
    showAppendToPdf: {
      type: Boolean,
      default: false,
    },
    allowReorder: {
      type: Boolean,
      default: false,
    },
    is_main: {
      type: Boolean,
      default: false,
    },
    hideAttach: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    reload(param) {
      if (param) {
        this.getFiles();
      }
    },
    typeId(param) {
      if (param) {
        this.getFiles();
      }
    },
  },
  data() {
    return {
      pageLoading: true,
      deleteLoading: false,
      deleteDialog: false,
      attachDialog: false,
      attachLoading: false,
      isReordering: false,
      isDragging: false,
      uuid: null,
      description: null,
      files: [],
      dbFiles: [],
      add_document_dialog: false,
      extensionArray: ["jpg", "png", "gif", "jpeg", "webp", "tiff", "jfif"],
    };
  },
  methods: {
    formatDateStartedNew(param) {
      return moment(param).format("hh:mm A");
    },
    getReminderDateClass(endDate, reminderDate) {
      const end = new Date(endDate);
      const reminder = new Date(reminderDate);
      return reminder > end ? "text-danger" : "";
    },
    init() {
      this.deleteLoading = false;
      this.deleteDialog = false;
      this.attachDialog = false;
      this.attachLoading = false;
      this.uuid = null;
      this.description = null;
      this.files = [];
    },
    getTypeName(type) {
      if (type == 2) {
        return "Internal";
      } else if (type == 5006) {
        return "Rejected";
      } else if (type == 5005) {
        return "Accepted";
      } else if (type == 301) {
        return "Admin use only";
      } else if (type == 5007) {
        return "Approved";
      } else if (type == 5008) {
        return "UnApproved";
      } else if (type == 302) {
        return "Internal use only";
      } else if (type == 5015) {
        return "Terminated";
      } else if (type == 678) {
        return "Overtime";
      }
    },
    getTypeColor(type) {
      if (type == 2) {
        return "#24326d";
      } else if (type == 5006) {
        return "red";
      } else if (type == 5005) {
        return "green";
      } else if (type == 5008) {
        return "red";
      } else if (type == 5007) {
        return "blue";
      } else if (type == 301) {
        return "red";
      } else if (type == 302) {
        return "green";
      } else if (type == 5015) {
        return "red";
      } else if (type == 678) {
        return "orange darken-2";
      }
    },
    updateFiles(param) {
      if (param) {
        const max_size = toNumber(this.$uploadSizeLIMIT);
        const max_file = toNumber(this.$uploadLIMIT);
        for (let i = 0; i < param.length; i++) {
          const size = round(toNumber(param[i].size / 1024), 2);
          if (size <= max_size && this.files.length < max_file) {
            this.files.push(param[i]);
          }
        }
      }
    },
    getFileSize(size) {
      return round(toNumber(size) / 1024, 2);
    },
    removeFile(index) {
      this.files.splice(index, 1);
      this.$refs["upload-file"].value = null;
    },
    maxFileSize() {
      return round(toNumber(this.$uploadSizeLIMIT) / 1024, 2);
    },
    selectFile() {
      this.$refs["upload-file"].reset();
      this.$refs["upload-file"].$refs["input"].value = null;
      this.$nextTick(() => {
        this.$refs["upload-file"].$refs["input"].click();
      });
    },
    doAction(row, param) {
      switch (param) {
        case "download":
          window.open(row.file.url, "_blank");
          break;
        case "delete":
          this.id = row.id;
          this.deleteDialog = true;
          break;
      }
    },
    deleteFile() {
      if (!this.id) {
        return false;
      }
      this.deleteLoading = true;
      ApiService.setHeader();
      ApiService.delete(`delete-documents/${this.id}`)

        .then(() => {
          this.getFiles();
        })
        .catch((error) => {
          this.$emit("error", error);
          this.logError(error);
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    updateAppendToPdf(row) {
      ApiService.setHeader();
      ApiService.patch(`file-manager/invoice-attachment/${row.id}`, {
        append_to_pdf: row.append_to_pdf,
      }).catch((error) => {
        row.append_to_pdf = !row.append_to_pdf;
        this.$emit("error", error);
        this.logError(error);
      });
    },
    getFiles() {
      ApiService.setHeader();
      ApiService.get(`documents/${this.type}/${this.typeId}`)

        .then(({ data }) => {
          this.dbFiles = data.data.map((file) => ({
            ...file,
            append_to_pdf: file.append_to_pdf == true,
          }));
          this.init();
          this.$emit("files", this.dbFiles);
          this.$emit("file-count", this.dbFiles.length);
        })
        .catch((error) => {
          this.$emit("files", []);
          this.$emit("file-count", 0);
          this.$emit("error", error);
          this.logError(error);
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },

    attachFile() {
      const params = new FormData();

      for (let i = 0; i < this.files.length; i++) {
        params.append(`file[${i}]`, this.files[i]);
      }

      params.append("description", this.description);

      this.attachLoading = true;

      ApiService.setHeader();
      ApiService.upload(`${this.type}/file`, params)
        .then(() => {
          this.getFiles();
          this.$refs["upload-file"].value = null;
        })
        .catch((error) => {
          this.$emit("error", error);
          this.logError(error);
        })
        .finally(() => {
          this.attachLoading = false;
        });
    },
    isImage(ext) {
      if (find(this.extensionArray, (row) => row == ext.toLowerCase())) {
        return true;
      } else {
        return false;
      }
    },
    checkWExpDate(date) {
      /* let currentData = MomentJS();
      let enddate = MomentJS(date, "DD/MM/YYYY");
      console.log(enddate.diff(currentData, "days"));
      if (enddate.diff(currentData, "days") > 0) {
        return false;
      }
      return true; */

      let currentData = moment();
      let enddate = moment(date, "YYYY-MM-DD");
      if (enddate.diff(currentData, "days") >= 0) {
        return false;
      }
      return true;
    },
    onReorderFiles() {
      if (!this.allowReorder || !this.dbFiles || this.dbFiles.length <= 1) {
        return;
      }
      this.isReordering = true;
      const document_ids = this.dbFiles.map((file) => file.id);
      ApiService.setHeader();
      ApiService.post("documents/reorder", {
        document_ids,
        type: this.type,
        parent_id: this.typeId,
      })
        .then(() => {
          this.$emit("reordered", this.dbFiles);
        })
        .catch((error) => {
          this.$emit("error", error);
          this.logError(error);
          this.getFiles();
        })
        .finally(() => {
          this.isReordering = false;
        });
    },
  },
  components: {
    Dialog,
    //	TextAreaInput,
    ImageTemplate,
    DocumentwithName,
    draggable,
  },
  mounted() {
    console.log(this.type);
    if (this.typeId > 0) {
      this.getFiles();
    }

    // EventBus.$on("reload:asset", () => {
    // 	this.getFiles();
    // });
  },
};
</script>
<style scoped>
.custom-min-height-60vh {
  height: 77vh;
}

.v-window {
  overflow: auto !important;
}

.v-chip.v-size--default.tag {
  height: auto !important;
  padding: 1px 8px !important;
}
</style>
<style>
#attachment .overflow-y {
  max-height: none !important;
}

.text-danger {
  color: red;
}

.document-drag-handle {
  cursor: grab !important;
}

.document-drag-handle:active {
  cursor: grabbing !important;
}

.document-drag-row {
  transition: background-color 0.15s ease;
}

.document-drag-row:hover {
  background-color: #f1f7fa !important;
}

.document-drag-row:hover .document-drag-handle {
  color: #00bcd4 !important;
}

.document-ghost {
  opacity: 0.5;
  background-color: #e0f7fa !important;
  border: 1px dashed #00bcd4 !important;
}

.document-chosen {
  background-color: #f1f5f9 !important;
}

.position-relative {
  position: relative;
}

.position-absolute-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
}

.doc-table-card {
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.doc-table-header {
  background-color: #f1f5fa;
  min-height: 44px;
}

.doc-header-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e3a8a;
  letter-spacing: 0.2px;
}

.doc-row {
  background-color: #ffffff;
  transition: background-color 0.15s ease;
}

.doc-row:hover {
  background-color: #f6f9fc !important;
}

.doc-row-highlight {
  background-color: #f8fafc;
}

.doc-row:hover .document-drag-handle {
  color: #00bcd4 !important;
}

.doc-icon-wrapper {
  width: 44px;
  height: 44px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.doc-title-link {
  color: #0b57d0 !important;
  text-decoration: none;
  transition: color 0.15s ease;
}

.doc-title-link:hover {
  color: #0842a0 !important;
  text-decoration: underline !important;
}

.doc-sub-details {
  color: #64748b;
  gap: 8px;
  line-height: 1.35;
}

.doc-pipe-divider {
  color: #cbd5e1;
  font-weight: 300;
  margin: 0 2px;
}

.doc-vertical-divider {
  width: 1px;
  height: 30px;
  background-color: #e2e8f0;
}

.document-action-group {
  gap: 4px;
  white-space: nowrap;
}

.append-pdf-column-header,
.append-pdf-column {
  width: 130px;
  min-width: 120px;
}

.append-pdf-column {
  display: flex;
  align-items: center;
  justify-content: center;
}

.append-pdf-unavailable {
  transform: translateX(-2px);
}

.document-action-group > .v-tooltip {
  display: flex;
  align-items: center;
}
</style>
